import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import { crossBtnGrey, THREE_DOTS_VERTICAL } from '../../assets/hostedassets';
import LoadingSpinner from '../_custom/LoadingSpinner/LoadingSpinner';
// Direct-file imports (not the `../_custom` barrel) — the barrel's unrelated
// heavy import graph (@uiw/react-color-name etc.) makes ANY consumer that
// transitively loads it (directly, or via `jest.requireActual` in a partial
// mock) fail to transform under Jest. Same fix SheetEditorDrawer.tsx already
// uses for its own Button import.
import Button from '../_custom/Button/Button';
import Modal from '../_custom/Modal/Modal';
import { formatDateByFormat, DATE_FORMATS } from '../../utils/dateUtils';
import styles from './HistorySideDrawer.module.scss';

export interface HistoryEntry {
    _id: string;
    userName: string;
    lastUpdatedAt: number;
    isRestored: boolean;
}

export interface HistorySideDrawerProps {
    containerId: string;
    isOpen: boolean;
    isExpanded: boolean;
    title: string;
    loading: boolean;
    historyEntries: HistoryEntry[];
    onClose: () => void;
    onToggleExpand: () => void;
    onSelectEntry: (entry: HistoryEntry) => void;
    onPreviewEntry?: (entry: HistoryEntry) => void;
    dateFormat?: string;
    previewLoading?: boolean;
    selectedEntryId?: string | null;
    onRestoreClick?: (entry: HistoryEntry) => void;
    onRestoreConfirm?: () => void;
    /** Replace the per-row 3-dot menu with a single Restore button in the pane
     *  footer, acting on the currently selected (previewed) row. */
    restoreInFooter?: boolean;
}
const CLOSE_ANIMATION_DURATION = 200;

function HistorySideDrawer({
    containerId,
    isOpen,
    isExpanded,
    title,
    loading,
    historyEntries,
    onClose,
    onToggleExpand,
    onSelectEntry,
    onPreviewEntry,
    dateFormat = DATE_FORMATS.TIME_DATE_FORMAT_HH_MM_A_DD_MMM_YYYY,
    previewLoading = false,
    selectedEntryId = null,
    onRestoreClick,
    onRestoreConfirm,
    restoreInFooter = false,
}: HistorySideDrawerProps) {
    const [showMenu, setShowMenu] = useState<string | null>(null);
    const [showRestoreModal, setShowRestoreModal] = useState(false);
    const [selectedEntry, setSelectedEntry] = useState<HistoryEntry | null>(null);
    const [isAnimating, setIsAnimating] = useState(false);
    const [shouldRender, setShouldRender] = useState(isOpen);

    // Handle click outside menu (moved outside useEffect)
    const handleClickOutside = useCallback((e: MouseEvent) => {
        const target = e.target as HTMLElement;
        if (!target.closest(`.${styles.MenuIcon}`) && !target.closest(`.${styles.MenuDropdown}`)) {
            setShowMenu(null);
        }
    }, []);

    // Handle opening and closing animations
    const handleAnimationState = useCallback(() => {
        if (isOpen) {
            setShouldRender(true);
            setIsAnimating(false);
            return;
        }

        if (shouldRender) {
            setIsAnimating(true);
            const timer = setTimeout(() => {
                setShouldRender(false);
                setIsAnimating(false);
            }, CLOSE_ANIMATION_DURATION);
            return () => clearTimeout(timer);
        }
    }, [isOpen, shouldRender]);

    useEffect(() => {
        return handleAnimationState();
    }, [handleAnimationState]);

    // Close menu when clicking outside
    useEffect(() => {
        if (!isOpen) return;
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen, handleClickOutside]);

    // Which row's preview is currently loading — set synchronously on click so
    // the spinner shows immediately, cleared once previewLoading settles.
    // selectedEntryId can't drive this: it only updates AFTER the fetch
    // resolves, so at click time it still points at the PREVIOUS row.
    const [pendingEntryId, setPendingEntryId] = useState<string | null>(null);
    useEffect(() => {
        if (!previewLoading) setPendingEntryId(null);
    }, [previewLoading]);

    const handleRowClick = useCallback(
        (entry: HistoryEntry) => {
            setPendingEntryId(entry._id);
            onPreviewEntry?.(entry);
        },
        [onPreviewEntry],
    );

    const handleThreeDotsClick = useCallback((e: React.MouseEvent, entryId: string) => {
        e.stopPropagation();
        setShowMenu((prev) => (prev === entryId ? null : entryId));
    }, []);

    const handleRestoreClick = useCallback((e: React.MouseEvent, entry: HistoryEntry) => {
        e.stopPropagation();
        setSelectedEntry(entry);
        setShowRestoreModal(true);
        setShowMenu(null);
    }, []);

    const handleConfirmRestore = useCallback(() => {
        if (selectedEntry) {
            onRestoreConfirm?.(); // Trigger Mixpanel tracking
            onSelectEntry(selectedEntry);
            setShowRestoreModal(false);
            setSelectedEntry(null);
        }
    }, [selectedEntry, onSelectEntry, onRestoreConfirm]);

    const handleCancelRestore = useCallback(() => {
        setShowRestoreModal(false);
        setSelectedEntry(null);
    }, []);

    // Footer mode: the button restores the currently selected (previewed) row.
    // The first entry IS the live content — restoring it would be a no-op fork,
    // so it never counts as restorable.
    const footerRestoreEntry = useMemo(() => {
        if (!restoreInFooter || !selectedEntryId) return null;
        if (historyEntries?.[0]?._id === selectedEntryId) return null;
        return historyEntries?.find((entry) => entry._id === selectedEntryId) ?? null;
    }, [restoreInFooter, selectedEntryId, historyEntries]);

    const handleFooterRestoreClick = useCallback(() => {
        if (!footerRestoreEntry) return;
        setSelectedEntry(footerRestoreEntry);
        setShowRestoreModal(true);
    }, [footerRestoreEntry]);

    const renderHistoryEntry = useCallback(
        (entry: HistoryEntry, index: number) => {
            const isLatest = index === 0;
            const isSelected = entry._id === selectedEntryId;
            const isLoadingThisEntry = previewLoading && pendingEntryId === entry._id;

            return (
                <div
                    key={entry._id}
                    data-testid="history-entry"
                    className={clsx(styles.HistoryEntry, {
                        [styles.HistoryEntry_Selected]: isSelected,
                    })}
                    onClick={() => handleRowClick(entry)}
                >
                    {isLoadingThisEntry ? (
                        <div className={styles.EntryLoadingIndicator} data-testid="history-entry-loading">
                            <LoadingSpinner color="var(--primary-text-colour)" height="0.875rem" />
                        </div>
                    ) : (
                        <div className={styles.CircleIndicator} />
                    )}
                    <div className={styles.EntryContent}>
                        {isLatest && entry.isRestored ? (
                            <>
                                <div className={styles.CurrentVersionLabel}>Current Version</div>
                                <div className={styles.UserInfo}>Restored, {formatDateByFormat(entry.lastUpdatedAt, dateFormat)}</div>
                                <div className={styles.UserInfo}>by {entry.userName}</div>
                            </>
                        ) : isLatest ? (
                            <>
                                <div className={styles.CurrentVersionLabel}>Current Version</div>
                                <div className={styles.UserInfo}>by {entry.userName}</div>
                            </>
                        ) : entry.isRestored ? (
                            <>
                                <div className={styles.UpdatedAt}>{formatDateByFormat(entry.lastUpdatedAt, dateFormat)}</div>
                                <div className={styles.UserInfo}>Restored, {formatDateByFormat(entry.lastUpdatedAt, dateFormat)}</div>

                                <div className={styles.UserInfo}>by {entry.userName}</div>
                            </>
                        ) : (
                            <>
                                <div className={styles.UpdatedAt}>{formatDateByFormat(entry.lastUpdatedAt, dateFormat)}</div>
                                <div className={styles.UserInfo}>by {entry.userName}</div>
                            </>
                        )}
                    </div>
                    {!isLatest && !restoreInFooter && (
                        <div className={styles.MenuContainer}>
                            <img
                                src={THREE_DOTS_VERTICAL}
                                alt="menu"
                                data-testid="history-entry-menu"
                                className={styles.MenuIcon}
                                onClick={(e) => handleThreeDotsClick(e, entry._id)}
                            />
                            {showMenu === entry._id && (
                                <div className={styles.MenuDropdown}>
                                    <div
                                        data-testid="history-restore"
                                        className={styles.MenuItem}
                                        onClick={(e) => handleRestoreClick(e, entry)}
                                    >
                                        Restore
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            );
        },
        [
            dateFormat,
            showMenu,
            handleRowClick,
            handleThreeDotsClick,
            handleRestoreClick,
            selectedEntryId,
            previewLoading,
            pendingEntryId,
            restoreInFooter,
        ],
    );

    const renderContent = useMemo(() => {
        if (loading) {
            return (
                <div className={styles.LoadingContainer}>
                    <LoadingSpinner color="var(--primary-text-colour)" height="1.875rem" />
                    <div className={styles.LoadingText}>Loading history...</div>
                </div>
            );
        }

        if (!historyEntries?.length) {
            return (
                <div className={styles.EmptyState}>
                    <div className={styles.EmptyText}>No history available</div>
                </div>
            );
        }

        return <div className={styles.HistoryList}>{historyEntries.map(renderHistoryEntry)}</div>;
    }, [loading, historyEntries, renderHistoryEntry]);

    if (!shouldRender) {
        return null;
    }

    const containerElement = document.getElementById(containerId);
    if (!containerElement) {
        return null;
    }

    return createPortal(
        <>
            <div className={clsx(styles.HistorySideDrawer, { [styles.Expanded]: isExpanded, [styles.Closing]: isAnimating })}>
                <div className={styles.ContentWrapper}>
                    <div className={styles.Header}>
                        <div className={styles.Title}>{title}</div>
                        <div className={styles.Actions}>
                            <img src={crossBtnGrey} alt="close" className={styles.CloseIcon} onClick={onClose} />
                        </div>
                    </div>
                    <div className={styles.Content}>{renderContent}</div>
                    {restoreInFooter && !loading && historyEntries?.length > 0 && (
                        <div className={styles.Footer}>
                            <Button
                                text="Restore"
                                variant="purple"
                                type="button"
                                className={styles.FooterButton}
                                onClick={handleFooterRestoreClick}
                                disabled={!footerRestoreEntry || previewLoading}
                                style={{ marginTop: '0' }}
                            />
                        </div>
                    )}
                </div>
            </div>

            {showRestoreModal && selectedEntry && (
                <Modal open={showRestoreModal} onClose={handleCancelRestore} size="flexible" className={styles.ConfirmModalOverlay}>
                    <div className={styles.ConfirmationModal}>
                        <div className={styles.ModalTitle}>Restore this version?</div>
                        <div className={styles.ModalSubtext}>
                            Are you sure you want to restore the version from{' '}
                            {formatDateByFormat(selectedEntry.lastUpdatedAt, DATE_FORMATS.TIME_DATE_FORMAT_H_MM_A_DD_MMMM_YYYY)}?
                        </div>
                        <div className={styles.ModalButtons}>
                            <Button text="Cancel" onClick={handleCancelRestore} variant="purpleinverted" style={{ marginTop: '0' }} />
                            <Button text="Restore" onClick={handleConfirmRestore} variant="primary" style={{ marginTop: '0' }} />
                        </div>
                    </div>
                </Modal>
            )}
        </>,
        containerElement,
    );
}

export default HistorySideDrawer;
